import { useStore } from "./store";

/**
 * Plays the pre-generated voice clips from /voice, one after another.
 * Clips that are not in the manifest (not generated yet) are skipped silently.
 */

/** Pending clips beyond this are dropped, oldest first, so speech never lags far behind fast bot turns. */
const MAX_PENDING = 2;
/** Lets the sound effect of the same batch (card flick, dice) play first. */
const START_DELAY_MS = 350;
const base = `${import.meta.env.BASE_URL}voice/`;

let available: Promise<Set<string>> | null = null;
const queue: string[] = [];
let current: HTMLAudioElement | null = null;
let starting = false;

function loadAvailable(): Promise<Set<string>> {
  available ??= fetch(`${base}manifest.json`)
    .then((r) => (r.ok ? (r.json() as Promise<{ clips?: Record<string, string> }>) : { clips: {} }))
    .then((m) => new Set(Object.keys(m.clips ?? {})))
    .catch(() => new Set<string>());
  return available;
}

const enabled = () => {
  const { soundOn, voiceOn } = useStore.getState();
  return soundOn && voiceOn;
};

function playNext() {
  current = null;
  const id = queue.shift();
  if (!id || !enabled()) return;
  const audio = new Audio(`${base}${id}.mp3`);
  current = audio;
  audio.onended = playNext;
  audio.onerror = playNext;
  // Rejected before the first user gesture or when the file is missing; move on either way.
  audio.play().catch(playNext);
}

/** Queues clips to be spoken. Does nothing when sound or voice is off. */
export function speak(ids: readonly string[]): void {
  if (ids.length === 0 || !enabled()) return;
  void loadAvailable().then((have) => {
    queue.push(...ids.filter((id) => have.has(id)));
    queue.splice(0, Math.max(0, queue.length - MAX_PENDING));
    if (current || starting || queue.length === 0) return;
    starting = true;
    setTimeout(() => {
      starting = false;
      if (!current) playNext();
    }, START_DELAY_MS);
  });
}

/** Stops the current clip and forgets the queue. */
export function stopSpeech(): void {
  queue.length = 0;
  if (current) {
    current.onended = current.onerror = null;
    current.pause();
    current = null;
  }
}

useStore.subscribe((s, prev) => {
  if ((prev.soundOn && !s.soundOn) || (prev.voiceOn && !s.voiceOn)) stopSpeech();
});
